import React, {useState} from 'react';
import {Button, Confirm, Input, Modal, Spinner, Switch} from "@tag/tag-components-react-v4";
import {CustomerDTO} from "../types/CustomerDTO";
import CustomerService from "../services/CustomerService";

import styles from './UnlinkWorkspaceModal.module.scss';
import {ProjectAccents} from "../../General/config/projectAccents";

interface UserMatch {
    userInformationId: string;
    username: string;
}

function UnlinkUserModal(props: Readonly<{
    visible: boolean,
    selectedCustomer: CustomerDTO,
    handleClose: Function,
    handleUnlinkWorkspaceModalClose: Function,
    handleCustomerMenuModalClose: Function,
    setAlert: Function
}>) {

    const [searchByUsername, setSearchByUsername] = useState(false);
    const [userId, setUserId] = useState('');
    const [username, setUsername] = useState('');
    const [loading, setLoading] = useState<string|undefined>(undefined);
    const [matches, setMatches] = useState<UserMatch[]>([]);
    const [userToUnlink, setUserToUnlink] = useState<UserMatch|undefined>(undefined);

    const isFindDisabled = searchByUsername ? !username.trim() : !userId.trim();

    const handleError = () => {
        props.setAlert({type: 'error', message: 'An error has occurred, please contact HSC Care DevOps'});
        props.handleClose();
        props.handleUnlinkWorkspaceModalClose();
        props.handleCustomerMenuModalClose();
    };

    const handleFind = async () => {
        setLoading('Searching...');
        setMatches([]);

        try {
            const result = searchByUsername
                ? await CustomerService.findUsersByUsername(props.selectedCustomer.clientId, username.trim(), props.selectedCustomer.environmentName)
                : await CustomerService.findUserById(props.selectedCustomer.clientId, userId.trim(), props.selectedCustomer.environmentName);

            if (!result) {
                props.setAlert({type: 'warning', message: 'No user found matching the details provided'});
                return;
            }

            if (searchByUsername) {
                setMatches(result as UserMatch[]);
            } else {
                setUserToUnlink(result as UserMatch);
            }
        } catch {
            handleError();
        } finally {
            setLoading(undefined);
        }
    };

    const handleUnlink = async () => {
        const user = userToUnlink;
        setUserToUnlink(undefined);

        if (!user) {
            return;
        }

        setLoading('Unlinking user...');

        try {
            const result = await CustomerService.unlinkUser(props.selectedCustomer.clientId, user.userInformationId, props.selectedCustomer.environmentName);

            if (!result) {
                props.setAlert({type: 'warning', message: `User '${user.userInformationId}' was not found for this customer`});
                return;
            }

            props.setAlert({type: 'success', message: `User '${user.username}' unlinked from Access Workspace`});
            props.handleClose();
            props.handleUnlinkWorkspaceModalClose();
            props.handleCustomerMenuModalClose();
        } catch {
            handleError();
        } finally {
            setLoading(undefined);
        }
    };

    const renderModalContent = () => {
        if (loading) {
            return (
                <div className={styles.UnlinkWorkspaceModalLoaderContainer}>
                    <Spinner>{loading}</Spinner>
                </div>
            );
        }

        return (
            <div style={{marginTop: '16px'}}>
                <div className={styles.UnlinkWorkspaceModalField}>
                    <Switch
                        checked={searchByUsername}
                        heading={searchByUsername ? 'Search by Username' : 'Search by ID'}
                        accent={ProjectAccents.PeoplePlanner}
                        onChange={(e) => {
                            setSearchByUsername(!!e.checked);
                            setMatches([]);
                        }}
                    />
                </div>
                {searchByUsername ?
                    <div className={styles.UnlinkWorkspaceModalField}>
                        <Input
                            placeholder="Username"
                            value={username}
                            onChange={(e) => setUsername(e.currentTarget.value)}
                            style={{width: '100%'}}
                        />
                    </div> :
                    <div className={styles.UnlinkWorkspaceModalField}>
                        <Input
                            placeholder="User Information ID"
                            value={userId}
                            onChange={(e) => setUserId(e.currentTarget.value)}
                            style={{width: '100%'}}
                        />
                    </div>
                }
                <div className={styles.UnlinkWorkspaceModalButtonElement}>
                    <Button text={'Find'} disabled={isFindDisabled} onClick={handleFind} />
                </div>
                {matches.length > 0 ?
                    <div style={{marginTop: '1em'}}>
                        {matches.map((user) => (
                            <div key={user.userInformationId} className={styles.UnlinkWorkspaceModalResultRow}>
                                <span>{user.username} ({user.userInformationId})</span>
                                <Button text={'Unlink'} onClick={() => setUserToUnlink(user)} />
                            </div>
                        ))}
                    </div> : undefined
                }
            </div>
        );
    };

    return (
        <>
            <Modal
                visible={props.visible && !userToUnlink}
                accent={ProjectAccents.PeoplePlanner}
                heading={'Unlink User'}
                onCancelButtonClick={() => props.handleClose()}
                onClose={() => props.handleClose()}
                primaryButtonProps={{buttonStyle: {'display': 'none'}}}
            >
                <div>
                    {renderModalContent()}
                </div>
            </Modal>
            {userToUnlink ?
                <Confirm
                    visible={!!userToUnlink}
                    type="warning"
                    accent={ProjectAccents.PeoplePlanner}
                    heading={'Are you sure?'}
                    onPrimaryButtonClick={handleUnlink}
                    onCancelButtonClick={() => setUserToUnlink(undefined)}
                    onClose={() => setUserToUnlink(undefined)}
                >
                    <div style={{lineHeight: '1.6'}}>
                        <p>This will unlink <strong>{userToUnlink.username}</strong> (ID {userToUnlink.userInformationId}) from their Access Workspace account.</p>
                        <p>Their workspace details will be cleared on both the corporate site and the customer database.</p>
                    </div>
                </Confirm> : undefined
            }
        </>
    );
}

export default UnlinkUserModal;
